import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { createShieldShotTheme } from "./theme";

const STORAGE_KEY = "shieldshot_theme_mode";

const ThemeModeContext = createContext({ mode: "light", setMode: () => {} });

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

const readInitialMode = () => {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
};

export default function ThemeModeProvider({ children }) {
  const [mode, setModeState] = useState(readInitialMode);

  // Keep <html data-theme> in sync so non-MUI CSS can theme with attribute selectors.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.dataset.theme = mode;
    }
  }, [mode]);

  const setMode = useCallback((next) => {
    const safe = next === "dark" ? "dark" : "light";
    try {
      window.localStorage.setItem(STORAGE_KEY, safe);
    } catch {
      /* ignore storage errors (private mode, quota, etc.) */
    }
    setModeState(safe);
  }, []);

  const theme = useMemo(() => createShieldShotTheme(mode), [mode]);
  const value = useMemo(() => ({ mode, setMode }), [mode, setMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
